// Roles de plataforma — única fuente de verdad del front (CC-CONSOLE #1024).
// Mismos ids que `apps/torre-de-control-api/functions/src/utils/platformRoles.js`.
//
// Jerarquía: owner (Propietario) > admin reseller > resto. Todo lo que el admin
// reseller puede hacer, el owner también: por eso los gates usan
// `isPlatformAdminUser()` / `TOP_ROLE_IDS` en vez de comparar un solo id.

/** Rol Propietario (owner): el más alto de la plataforma. */
export const OWNER_ROLE_ID = "ZHHsDgLrhRyjsgBJusmR";

/** Role ID del admin_reseller. */
export const ADMIN_RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ";

/** Roles de plataforma (owner + admin reseller), del más alto al más bajo. */
export const TOP_ROLE_IDS: string[] = [OWNER_ROLE_ID, ADMIN_RESELLER_ROLE_ID];

/**
 * Dominios de correo que pueden tener un rol de plataforma (owner / admin
 * reseller). Espejo de `PRIVILEGED_ROLE_EMAIL_DOMAINS` en
 * `apps/torre-de-control-api/functions/src/utils/privilegedRoleDomains.js`.
 * Aquí sólo sirve para no OFRECER el rol: quien decide es el API, que responde
 * 403 `PRIVILEGED_ROLE_DOMAIN_REQUIRED`. Es una restricción, no concede nada.
 */
export const PRIVILEGED_ROLE_EMAIL_DOMAINS: readonly string[] = [
  "codingcloud.online",
  "wayak-logistics.com",
];

/**
 * ¿El correo es de un dominio permitido para roles de plataforma? Comparación
 * exacta del dominio (sin subdominios ni sufijos) y sin distinguir mayúsculas.
 */
export const isPrivilegedRoleEmail = (email?: string | null): boolean => {
  if (typeof email !== "string") return false;
  const normalized = email.trim().toLowerCase();
  if (!normalized || /\s/.test(normalized)) return false;
  const at = normalized.lastIndexOf("@");
  if (at <= 0 || at !== normalized.indexOf("@")) return false;
  return PRIVILEGED_ROLE_EMAIL_DOMAINS.includes(normalized.slice(at + 1));
};

const currentRoleId = (): string => {
  try {
    return localStorage.getItem("userRoleId") || "";
  } catch {
    return "";
  }
};

/** ¿El rol es owner? */
export const isOwnerRoleId = (roleId?: string | null): boolean =>
  roleId === OWNER_ROLE_ID;

/** ¿El rol es de plataforma (owner o admin reseller)? */
export const isPlatformAdminRoleId = (roleId?: string | null): boolean =>
  !!roleId && TOP_ROLE_IDS.includes(roleId);

/** ¿La sesión actual es de un owner? */
export const isOwnerUser = (): boolean => isOwnerRoleId(currentRoleId());

/** ¿La sesión actual es de un owner o de un admin reseller? */
export const isPlatformAdminUser = (): boolean =>
  isPlatformAdminRoleId(currentRoleId());

/**
 * Determina si el usuario actual es admin de plataforma (owner o admin
 * reseller) basándose en su ROL, no en su compañía. Un usuario puede
 * pertenecer a la compañía reseller pero tener un rol de admin regular.
 */
export function useIsReseller() {
  const isReseller = isPlatformAdminUser();

  return {
    isReseller,
    isOwner: isOwnerUser(),
    canEdit: isReseller,
    isDisabled: !isReseller,
    inputProps: {
      disabled: !isReseller,
      readOnly: !isReseller,
    },
    selectProps: {
      isDisabled: !isReseller,
    },
  };
}

export default useIsReseller;

/** Mismo que isPlatformAdminRoleId: nombre que usan las pantallas sincronizadas desde CC-CONSOLE. */
export const isTopRoleId = isPlatformAdminRoleId;
